import React from 'react'

import { cn, useFilter } from '@rewardops/ui'

import { handleEnterKeyPress } from '~/utils/event-handler-utils'
import { ExtendSort, getCustomLocalisedSortOptions } from '~/utils/filter-utils'
import { MapleSelect } from '~/components/maple/components/select'
import ResponsiveComponent from '~/components/responsive-component'
import { SortOptionType } from '~/app/common/enums'
import { useTranslation } from '~/app/i18n/client'

export interface SortOptions {
  name: SortOptionType
  messageName: string
  order: 'ASCENDING' | 'DESCENDING'
}

interface GenericSortMenuProps {
  inputType?: 'select' | 'list'
  onSelectionSet?: Function
  sortOptions: SortOptions[]
  defaultOption?: string
}

export const GenericSortMenu = ({
  inputType = 'select',
  onSelectionSet,
  sortOptions,
  defaultOption = undefined,
}: GenericSortMenuProps) => {
  const { t } = useTranslation()
  const { sort, setSort } = useFilter()

  const defaultValue =
    getCustomLocalisedSortOptions(sortOptions).find((o) => o.key === sort)
      ?.key ?? defaultOption

  const options = getCustomLocalisedSortOptions(sortOptions).map((option) => ({
    key: option.key,
    value: option.message,
  }))

  if (inputType === 'list') {
    return (
      <div className="flex flex-col gap-3 py-4">
        {getCustomLocalisedSortOptions(sortOptions)?.map((option) => {
          const isSelected = option.key === sort
          return (
            <SortListItem
              onSelectionSet={onSelectionSet}
              setSort={setSort}
              selected={isSelected}
              option={option}
            />
          )
        })}
      </div>
    )
  }

  const desktopComponent = (
    <MapleSelect
      id="sort-select"
      className="max-w-[280px] justify-end"
      placeholder={t('filters.sortBy')}
      defaultValue={defaultValue}
      options={options}
      onValueChange={(s) => setSort(s)}
    />
  )

  const mobileComponent = (
    <MapleSelect
      id="sort-select"
      className="w-auto justify-start bg-[#F5F5F5] px-4 py-2"
      placeholder={t('filters.sortBy')}
      defaultValue={defaultValue}
      options={options}
      onValueChange={(s) => setSort(s)}
    />
  )

  return (
    <ResponsiveComponent
      desktopComponent={desktopComponent}
      mobileComponent={mobileComponent}
    />
  )
}

function SortListItem({
  onSelectionSet,
  selected,
  option,
  setSort,
}: {
  onSelectionSet?: Function
  selected: boolean
  option: ExtendSort
  setSort: any
}) {
  const handleKeyDown = () => {
    setSort(option.key)
    if (onSelectionSet) onSelectionSet()
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    setSort(option.key)
    if (onSelectionSet) onSelectionSet()
  }

  return (
    <div
      id="sort-select"
      role="button"
      tabIndex={0}
      className={cn('cursor-pointer items-center hover:text-slate-400', {
        'font-semibold': selected,
      })}
      onKeyDown={(e) => handleEnterKeyPress(e, handleKeyDown)}
      onClick={handleClick}
    >
      {option.message}
    </div>
  )
}
